import {
	Alert,
	Badge,
	Box,
	Button,
	Divider,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	SwitchInput,
	Table,
	TableFooterDataCell,
	Text,
} from "@sembark-travel/ui-base"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { ButtonLink } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import {
	endOf,
	formatDate,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import { formatMoneyByIntl, moneyParseByDecimal } from "@sembark-travel/money"
import { numberToLocalString } from "@sembark-travel/number-utils"
import { useMemo, useState } from "react"
import useSWR from "swr"
import config from "../config"
import { generatePath, Link } from "../router-utils"
import { JSONPreview } from "./JsonPreview"
import { WhatsAppConnectionHealth } from "./MessagingGateways"
import {
	templateFiltersToLocationQuery,
	useFetchWAIntegratedApp,
	type TFBBusinessAccount,
	type TWAIntegratedApp,
	type TWAPricingAnalytics,
	type TWAPricingDataPoint,
	type TWhatsappBusinessAccount,
	type TWhatsappBusinessAccountPhoneNumber,
} from "./store"
import { PlatformIcon } from "./PlatformIcon"

/**
 * Everything Sembark holds about a WhatsApp integration's Meta side: the
 * authorisation, the business, its accounts and each account's numbers.
 * Local data only — the health dialog is the live view, and Sync refreshes
 * what is shown here.
 */
export function WhatsAppAccountsDetails({
	integratedAppId,
	integrationId,
}: {
	integratedAppId: number
	/** The integration's uid, for links into the sibling tabs. */
	integrationId: string
}) {
	const { data, error, mutate } = useFetchWAIntegratedApp(integratedAppId)

	if (!data) {
		if (error) {
			return (
				<Alert status="error">
					{error?.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}

	const integratedApp = data.data
	const businesses = groupByBusiness(integratedApp.wa_business_accounts || [])

	return (
		<Stack gap="6" padding="4">
			{businesses.length === 0 ? (
				<Alert status="info">
					No Meta business is linked yet. Connect the WhatsApp account above, or
					use Sync if it was connected recently.
				</Alert>
			) : (
				<>
					{businesses.map(({ business, accounts }) => (
						<BusinessDetails
							key={business?.id ?? "unknown"}
							business={business}
							accounts={accounts}
							integratedApp={integratedApp}
							integrationId={integrationId}
						/>
					))}
					<Divider marginY="0" />
				</>
			)}

			<ConnectionDetails integratedApp={integratedApp} refresh={mutate} />
		</Stack>
	)
}

type TBusinessGroup = {
	business: TFBBusinessAccount | undefined
	accounts: Array<TWhatsappBusinessAccount>
}

/**
 * Accounts arrive flat with their business embedded; the page reads better
 * business-first.
 */
function groupByBusiness(
	accounts: Array<TWhatsappBusinessAccount>
): Array<TBusinessGroup> {
	const groups = new Map<number | string, TBusinessGroup>()
	for (const account of accounts) {
		const key =
			account.fb_business_account?.id ?? account.fb_business_account_id
		const group = groups.get(key) ?? {
			business: account.fb_business_account,
			accounts: [],
		}
		group.accounts.push(account)
		groups.set(key, group)
	}
	return Array.from(groups.values())
}

function ConnectionDetails({
	integratedApp,
	refresh,
}: {
	integratedApp: TWAIntegratedApp
	refresh: () => void
}) {
	const method = integratedApp.connection_method
	const expiresAt = integratedApp.user_access_token_expires_at
	const embeddedSignup = integratedApp.embedded_signup

	return (
		<Stack gap="4">
			<Inline
				justifyContent="between"
				alignItems="center"
				gap="4"
				flexWrap="wrap"
			>
				<Heading as="h3" fontSize="md">
					Connection
				</Heading>
				<Inline gap="2" flexWrap="wrap">
					<SyncWithMetaButton
						integratedAppId={integratedApp.id}
						onQueued={refresh}
					/>
					<WhatsAppConnectionHealth integratedAppId={integratedApp.id}>
						{({ openDialog }) => (
							<Button level="secondary" size="sm" onClick={openDialog}>
								<Icons.ShieldCheck /> Check Connection Health
							</Button>
						)}
					</WhatsAppConnectionHealth>
				</Inline>
			</Inline>
			<DefinitionList
				items={[
					[
						"Connected through",
						method ? (
							<Badge
								title={
									method === "embedded_signup"
										? "Meta's Embedded Signup dialog. The token does not expire."
										: "Facebook Login. The token is refreshed on a schedule."
								}
							>
								{method === "embedded_signup"
									? "Embedded Signup"
									: "Facebook Login"}
							</Badge>
						) : (
							<Text color="muted">Not connected</Text>
						),
					],
					[
						"Authorised on",
						integratedApp.user_access_token_issued_at ? (
							formatDate(
								integratedApp.user_access_token_issued_at,
								config.dateTimeDisplayFormat
							)
						) : (
							<Text color="muted">—</Text>
						),
					],
					[
						"Token expires",
						!method ? (
							<Text color="muted">—</Text>
						) : expiresAt ? (
							formatDate(expiresAt, config.dateTimeDisplayFormat)
						) : (
							<Text>Does not expire</Text>
						),
					],
					[
						"Facebook user id",
						integratedApp.user_id ? (
							<Copyable value={String(integratedApp.user_id)} />
						) : (
							<Text color="muted">—</Text>
						),
					],
					[
						"Permissions",
						integratedApp.scopes?.length ? (
							<Inline gap="1" flexWrap="wrap">
								{integratedApp.scopes.map((scope) => (
									<Badge key={scope} outlined>
										{scope}
									</Badge>
								))}
							</Inline>
						) : (
							<Text color="muted">—</Text>
						),
					],
					[
						"Default team",
						integratedApp.default_team ? (
							<Text color="accent">
								<Link
									to="/teams/:teamId"
									params={{ teamId: String(integratedApp.default_team.id) }}
								>
									{integratedApp.default_team.name}
								</Link>
							</Text>
						) : (
							<Text color="muted">
								Not set — new conversations go to all sales heads
							</Text>
						),
					],
					...(integratedApp.provider === "whatsapp_cloud"
						? [
								[
									"Test mode",
									integratedApp.integration?.in_test_mode_at ? (
										<Stack gap="1" key="test-mode">
											<Inline gap="2" alignItems="center" flexWrap="wrap">
												<Badge warning>On</Badge>
												<Text>
													since{" "}
													{formatDate(
														integratedApp.integration.in_test_mode_at,
														config.dateTimeDisplayFormat
													)}
												</Text>
											</Inline>
											{integratedApp.test_recipient ? (
												<Inline gap="2" alignItems="center" flexWrap="wrap">
													<Text>Every message is delivered to</Text>
													<Copyable value={integratedApp.test_recipient} />
													<Text>instead of the customer.</Text>
												</Inline>
											) : (
												<Text color="danger">
													No test number is set, so every send is refused.
												</Text>
											)}
										</Stack>
									) : (
										<Inline
											gap="2"
											alignItems="center"
											flexWrap="wrap"
											key="test-mode"
										>
											<Text color="muted">Off — messages go to customers.</Text>
											{integratedApp.test_recipient ? (
												<Text color="muted">
													Remembered test number: +
													{integratedApp.test_recipient}
												</Text>
											) : null}
										</Inline>
									),
								] as [React.ReactNode, React.ReactNode],
							]
						: []),
					...(embeddedSignup
						? [
								[
									"Embedded Signup allocated",
									<Stack gap="1" key="es">
										<IdLine
											label="Business"
											value={embeddedSignup.business_id}
										/>
										<IdLine label="WABA" value={embeddedSignup.waba_id} />
										<IdLine
											label="Number"
											value={embeddedSignup.phone_number_id}
										/>
									</Stack>,
								] as [React.ReactNode, React.ReactNode],
							]
						: []),
				]}
			/>
		</Stack>
	)
}

function BusinessDetails({
	business,
	accounts,
	integratedApp,
	integrationId,
}: {
	business: TFBBusinessAccount | undefined
	accounts: Array<TWhatsappBusinessAccount>
	integratedApp: TWAIntegratedApp
	integrationId: string
}) {
	const verification = (business?.verification_status || "").toLowerCase()

	return (
		<Stack gap="4">
			<Inline alignItems="center" gap="4" flexWrap="wrap">
				<Box>
					<PlatformIcon platform="meta" />
				</Box>
				<Stack gap="1" flex="1" minWidth="0">
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						<Heading as="h3" fontSize="md">
							{business?.name ?? "Meta business"}
						</Heading>
						{business ? (
							<Badge
								success={verification === "verified"}
								warning={verification === "pending"}
								danger={
									verification !== "verified" && verification !== "pending"
								}
							>
								{(business.verification_status || "not_verified").toUpperCase()}
							</Badge>
						) : null}
					</Inline>
					{business ? (
						<Inline
							fontSize="sm"
							color="muted"
							gap="4"
							alignItems="center"
							flexWrap="wrap"
						>
							<Inline gap="1" alignItems="center">
								<Text>Business ID:</Text>
								<Copyable value={String(business.fb_business_account_id)} />
							</Inline>
							<Text
								as="a"
								href={`https://business.facebook.com/settings/whatsapp-business-accounts?business_id=${business.fb_business_account_id}`}
								target="_blank"
								rel="noopener noreferrer"
								fontSize="sm"
								color="accent"
							>
								Open in Meta Business Settings <Icons.ArrowTopRightOnSquare />
							</Text>
						</Inline>
					) : null}
				</Stack>
			</Inline>
			{!accounts.length ? (
				<Box color="muted">
					No WhatsApp Business Accounts are linked to this Meta business.
				</Box>
			) : (
				<Stack gap="6" paddingLeft="3">
					{accounts.map((account) => (
						<Box key={account.id}>
							<AccountDetails
								account={account}
								businessId={business?.fb_business_account_id}
								integratedApp={integratedApp}
								integrationId={integrationId}
							/>
						</Box>
					))}
				</Stack>
			)}
		</Stack>
	)
}

function AccountDetails({
	account,
	businessId,
	integratedApp,
	integrationId,
}: {
	account: TWhatsappBusinessAccount
	businessId?: number | string
	integratedApp: TWAIntegratedApp
	integrationId: string
}) {
	const review = account.account_review_status
	const managerUrl = `https://business.facebook.com/latest/whatsapp_manager/phone_numbers?business_id=${businessId ?? ""}&asset_id=${account.wa_business_account_id}`
	const templatesCount = account.wa_message_templates_count ?? 0

	return (
		<Stack gap="3">
			<Inline alignItems="start" gap="2" flexWrap="wrap">
				<Box paddingTop="1">
					<PlatformIcon platform="whatsapp_cloud_api" size="18" />
				</Box>
				<Stack gap="1" flex="1" minWidth="0">
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						<Text fontWeight="semibold">{account.name}</Text>
						<Badge
							success={review === "APPROVED"}
							warning={review === "PENDING" || review === "RESTRICTED"}
							danger={review === "REJECTED"}
						>
							{review}
						</Badge>
					</Inline>
					<Inline
						fontSize="sm"
						color="muted"
						gap="1"
						alignItems="center"
						flexWrap="wrap"
					>
						<Inline gap="1" alignItems="center">
							<Text>WABA ID:</Text>
							<Copyable value={String(account.wa_business_account_id)} />
						</Inline>
						<Text>•</Text>
						<ButtonLink
							to={
								generatePath(
									"/integrations/:integrationId/whatsapp/templates",
									{
										integrationId,
									}
								) +
								"?" +
								new URLSearchParams(
									templateFiltersToLocationQuery({
										waba: account,
									}) as Record<string, string>
								).toString()
							}
							inline
						>
							{templatesCount} {templatesCount === 1 ? "template" : "templates"}
						</ButtonLink>
						<Text>•</Text>
						<Text
							as="a"
							href={managerUrl}
							target="_blank"
							rel="noopener noreferrer"
							fontSize="sm"
							color="accent"
						>
							Open in WhatsApp Manager <Icons.ArrowTopRightOnSquare />
						</Text>
					</Inline>
				</Stack>
				{integratedApp.provider === "whatsapp_cloud" ? (
					<WABusinessAccountPricing account={account}>
						{({ openDialog }) => (
							<Button level="secondary" size="sm" onClick={openDialog}>
								<Icons.BankNotes /> Pricing
							</Button>
						)}
					</WABusinessAccountPricing>
				) : null}
			</Inline>
			<Box paddingLeft="6">
				{account.wa_phone_numbers.length === 0 ? (
					<Text color="muted" fontSize="sm">
						No phone numbers under this account yet. Add one in WhatsApp
						Manager, then Sync.
					</Text>
				) : (
					<Table
						headers={[
							"Number",
							"Verified name",
							"Status",
							"Quality",
							"Tier",
							"Mode",
							"Official",
							"Welcome messages",
							"Two-step PIN",
							"",
						]}
						alignCols={{ 9: "right" }}
						responsive
						bordered
						hover
						rows={account.wa_phone_numbers.map((phone) => [
							<PhoneNumberCell
								key="number"
								phone={phone}
								isDefault={integratedApp.wa_phone_number?.id === phone.id}
							/>,
							phone.name,
							<Badge
								key="status"
								success={phone.status === "CONNECTED"}
								danger={phone.status !== "CONNECTED"}
							>
								{phone.status}
							</Badge>,
							<Badge
								key="quality"
								success={phone.quality_rating === "GREEN"}
								warning={phone.quality_rating === "YELLOW"}
								danger={phone.quality_rating === "RED"}
							>
								{phone.quality_rating}
							</Badge>,
							phone.messaging_limit_tier,
							phone.account_mode === "LIVE" ? (
								"LIVE"
							) : (
								<Badge key="mode" warning>
									{phone.account_mode}
								</Badge>
							),
							phone.is_official_business_account ? (
								<Icons.OkCircle key="oba" title="Official Business Account" />
							) : (
								<Text key="oba" color="muted">
									—
								</Text>
							),
							<WelcomeMessages
								// Remount when a sync brings a new value from Meta.
								key={`welcome-${String(phone.welcome_messages_enabled)}`}
								integratedAppId={integratedApp.id}
								phone={phone}
							/>,
							<TwoStepPin key="pin" pin={phone.two_step_pin ?? null} />,
							<Text
								key="link"
								as="a"
								href={managerUrl}
								target="_blank"
								rel="noopener noreferrer"
								fontSize="sm"
								color="accent"
							>
								WhatsApp Manager <Icons.ArrowTopRightOnSquare size="3" />
							</Text>,
						])}
					/>
				)}
			</Box>
		</Stack>
	)
}

function PhoneNumberCell({
	phone,
	isDefault,
}: {
	phone: TWhatsappBusinessAccountPhoneNumber
	isDefault: boolean
}) {
	return (
		<Stack gap="1">
			<Inline gap="2" alignItems="center">
				<Text fontWeight="semibold">{phone.display_phone_number}</Text>
				{isDefault ? (
					<Badge
						primary
						title="Used when an automation or message does not name a sender."
					>
						Default sender
					</Badge>
				) : null}
			</Inline>
			<Text fontSize="sm" color="muted">
				Id: <Copyable value={String(phone.wa_phone_number_id)} />
			</Text>
		</Stack>
	)
}

/**
 * Meta's welcome messages setting on a number. While it is off, a customer
 * who opens the chat from an ad without typing never reaches us, so the
 * greeting automation cannot answer them. Flipping it changes it at Meta
 * first; the switch shows what Meta took.
 */
function WelcomeMessages({
	integratedAppId,
	phone,
}: {
	integratedAppId: number
	phone: TWhatsappBusinessAccountPhoneNumber
}) {
	const xhr = useXHR()
	const [enabled, setEnabled] = useState(
		Boolean(phone.welcome_messages_enabled)
	)
	const [isSaving, setIsSaving] = useState(false)

	return (
		<Inline
			as="label"
			gap="2"
			alignItems="center"
			title={
				enabled
					? "Customers who open the chat from an ad or a link without typing reach you and can be greeted."
					: "Customers who open the chat from an ad or a link without typing will not reach you until this is on."
			}
		>
			<SwitchInput
				name={`welcome_messages_${phone.id}`}
				checked={enabled}
				disabled={isSaving}
				onChange={async (checked) => {
					setIsSaving(true)
					try {
						const { data } = await xhr.patch<{
							data: TWhatsappBusinessAccountPhoneNumber
						}>(
							`/integrations/whatsapp/${integratedAppId}/phone-numbers/${phone.id}/welcome-messages`,
							{ enabled: checked ? 1 : 0 }
						)
						setEnabled(Boolean(data.data.welcome_messages_enabled))
						showSnackbar(
							data.data.welcome_messages_enabled
								? "Welcome messages turned on."
								: "Welcome messages turned off."
						)
					} catch (error) {
						showSnackbar(
							(error as Error)?.message || "Could not change welcome messages."
						)
					} finally {
						setIsSaving(false)
					}
				}}
			/>
			<Text fontSize="sm" color={enabled ? undefined : "warning"}>
				{enabled ? "On" : "Off"}
			</Text>
		</Inline>
	)
}

/**
 * Masked until asked for. WhatsApp Manager wants this PIN before it will
 * delete a connected number, which is the one time an operator needs it.
 */
function TwoStepPin({ pin }: { pin: string | null }) {
	const [revealed, setRevealed] = useState(false)

	if (!pin) {
		return (
			<Text
				color="muted"
				fontSize="sm"
				title="Registered outside Sembark, or not registered yet."
			>
				Not set by Sembark
			</Text>
		)
	}

	return (
		<Inline gap="2" alignItems="center">
			<Text as="code" fontSize="sm">
				{revealed ? pin : "••••••"}
			</Text>

			<Button
				level="tertiary"
				size="sm"
				onClick={() => setRevealed((value) => !value)}
				title={revealed ? "Hide" : "Reveal"}
			>
				<Icons.Eye />
			</Button>
			<CopyToClipboard>
				{({ copyTextToClipboard }) => (
					<CopyToClipboardButton
						onClick={() => copyTextToClipboard(pin)}
						iconOnly
						size="sm"
						level="tertiary"
					>
						<Icons.ClipboardCopy size="3" />
					</CopyToClipboardButton>
				)}
			</CopyToClipboard>
		</Inline>
	)
}

function Copyable({ value }: { value: string }) {
	return (
		<Box display="inlineFlex" as="span" gap="1" alignItems="center">
			<Text as="code" fontSize="sm">
				{value}
			</Text>
			<CopyToClipboard>
				{({ copyTextToClipboard }) => (
					<CopyToClipboardButton
						onClick={() => copyTextToClipboard(value)}
						iconOnly
						inline
					>
						<Icons.ClipboardCopy />
					</CopyToClipboardButton>
				)}
			</CopyToClipboard>
		</Box>
	)
}

function SyncWithMetaButton({
	integratedAppId,
	onQueued,
}: {
	integratedAppId: number
	onQueued: () => void
}) {
	const xhr = useXHR()
	const [isSyncing, setIsSyncing] = useState(false)

	return (
		<Button
			level="secondary"
			size="sm"
			disabled={isSyncing}
			onClick={async () => {
				setIsSyncing(true)
				try {
					await xhr.post(`/integrations/whatsapp/${integratedAppId}/sync`)
					showSnackbar("Syncing with Meta. This takes a few seconds.")
					onQueued()
				} catch (error) {
					showSnackbar((error as Error)?.message || "Could not start the sync.")
				} finally {
					setIsSyncing(false)
				}
			}}
		>
			<Icons.Refresh /> {isSyncing ? "Syncing…" : "Sync with Meta"}
		</Button>
	)
}

function IdLine({ label, value }: { label: string; value?: string }) {
	if (!value) return null
	return (
		<Text fontSize="sm">
			{label}: <Copyable value={value} />
		</Text>
	)
}

function DefinitionList({
	items,
}: {
	items: Array<[React.ReactNode, React.ReactNode]>
}) {
	return (
		<Box as="dl" margin="0">
			<Stack gap="2">
				{items.map(([term, definition], index) => (
					<Inline key={index} gap="4" alignItems="center" flexWrap="wrap">
						<Box as="dt" style={{ minWidth: 180 }} margin="0">
							<Box fontSize="sm" color="muted" paddingY="1">
								{term}
							</Box>
						</Box>
						<Box as="dd" flex="1" minWidth="0" margin="0">
							<Box fontSize="sm">{definition}</Box>
						</Box>
					</Inline>
				))}
			</Stack>
		</Box>
	)
}

type TPricingPeriod = "this_month" | "last_month" | "last_30_days"

const PRICING_PERIODS: Array<{ value: TPricingPeriod; label: string }> = [
	{ value: "this_month", label: "This month" },
	{ value: "last_month", label: "Last month" },
	{ value: "last_30_days", label: "Last 30 days" },
]

function pricingPeriodRange(period: TPricingPeriod): {
	start: string
	end: string
} {
	const today = new Date()
	switch (period) {
		case "last_month": {
			const lastMonth = subtractUnit(today, 1, "month")
			return {
				start: formatDate(startOf(lastMonth, "month"), "YYYY-MM-DD"),
				end: formatDate(endOf(lastMonth, "month"), "YYYY-MM-DD"),
			}
		}
		case "last_30_days":
			return {
				start: formatDate(subtractUnit(today, 29, "day"), "YYYY-MM-DD"),
				end: formatDate(today, "YYYY-MM-DD"),
			}
		case "this_month":
		default:
			return {
				start: formatDate(startOf(today, "month"), "YYYY-MM-DD"),
				end: formatDate(today, "YYYY-MM-DD"),
			}
	}
}

/**
 * What Meta charged a WhatsApp business account, read live from Meta each
 * time the dialog opens. Nothing is stored; the raw response is shown too so
 * the shape can be studied before deciding what to keep.
 */
function WABusinessAccountPricing({
	account,
	children,
}: {
	account: TWhatsappBusinessAccount
	children: (props: { openDialog: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [period, setPeriod] = useState<TPricingPeriod>("this_month")
	const [granularity, setGranularity] =
		useState<TWAPricingAnalytics["granularity"]>("DAILY")

	const query = new URLSearchParams({
		...pricingPeriodRange(period),
		granularity,
	}).toString()
	const { data, error, isValidating } = useSWR(
		isOpen
			? `/wa-business-accounts/${account.id}/pricing-analytics?${query}`
			: null,
		(cacheKey) =>
			xhr
				.get<{ data: TWAPricingAnalytics }>(cacheKey)
				.then((resp) => resp.data.data),
		{ revalidateOnFocus: false }
	)

	return (
		<>
			{children({ openDialog })}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title={`WhatsApp Pricing · ${account.name}`}
				lg
			>
				<Dialog.Body>
					<Stack gap="4">
						<Inline gap="4" justifyContent="between" flexWrap="wrap">
							<Inline gap="1" flexWrap="wrap">
								{PRICING_PERIODS.map((option) => (
									<Button
										key={option.value}
										size="sm"
										level={period === option.value ? "primary" : "secondary"}
										onClick={() => setPeriod(option.value)}
									>
										{option.label}
									</Button>
								))}
							</Inline>
							<Inline gap="1">
								{(["DAILY", "MONTHLY"] as const).map((value) => (
									<Button
										key={value}
										size="sm"
										level={granularity === value ? "primary" : "secondary"}
										onClick={() => setGranularity(value)}
									>
										{value === "DAILY" ? "Daily" : "Monthly"}
									</Button>
								))}
							</Inline>
						</Inline>
						{isValidating && !data ? (
							<Spinner alignCenter padding="4" />
						) : error ? (
							<Alert status="error">
								{error?.message || "Could not read the pricing from Meta."}
							</Alert>
						) : data ? (
							<PricingDetails analytics={data} />
						) : null}
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function PricingDetails({ analytics }: { analytics: TWAPricingAnalytics }) {
	const currency = analytics.currency
	const points = analytics.data_points

	const summary = useMemo(() => {
		const groups = new Map<
			string,
			{ category: string; type: string; volume: number; cost: number }
		>()
		for (const point of points) {
			const category = point.pricing_category || "N/A"
			const type = point.pricing_type || "N/A"
			const key = `${category}:${type}`
			const group = groups.get(key) ?? { category, type, volume: 0, cost: 0 }
			group.volume += point.volume ?? 0
			group.cost += point.cost ?? 0
			groups.set(key, group)
		}
		return Array.from(groups.values()).sort((a, b) => b.cost - a.cost)
	}, [points])

	const totalVolume = summary.reduce((sum, group) => sum + group.volume, 0)
	const totalCost = summary.reduce((sum, group) => sum + group.cost, 0)

	function formatCost(cost: number | undefined) {
		if (cost === undefined || cost === null) return "-"
		return currency
			? formatMoneyByIntl(moneyParseByDecimal(cost, currency), {
					showCurrency: true,
					keepTrailingZero: true,
				})
			: numberToLocalString(cost)
	}

	if (points.length === 0) {
		return (
			<Stack gap="4">
				<Alert status="info">Meta reported no messages for this period.</Alert>
				<PricingRawResponse analytics={analytics} />
			</Stack>
		)
	}

	return (
		<Stack gap="6">
			<Stack gap="2">
				<Inline gap="2" alignItems="center" justifyContent="between">
					<Text fontWeight="semibold">Summary</Text>
					<Text fontSize="sm" color="muted">
						{formatDate(analytics.start, "D MMM, YYYY")} –{" "}
						{formatDate(analytics.end, "D MMM, YYYY")}
						{currency ? ` · ${currency}` : ""}
					</Text>
				</Inline>
				<Table
					headers={["Category", "Type", "Messages", "Cost"]}
					alignCols={{ 2: "right", 3: "right" }}
					responsive
					bordered
					rows={summary.map((group) => [
						<PricingLabel value={group.category} />,
						<PricingLabel value={group.type} />,
						numberToLocalString(group.volume, 0),
						formatCost(group.cost),
					])}
				>
					<tfoot>
						<tr>
							<TableFooterDataCell colSpan={2}>Total</TableFooterDataCell>
							<TableFooterDataCell textAlign="right">
								{numberToLocalString(totalVolume, 0)}
							</TableFooterDataCell>
							<TableFooterDataCell textAlign="right">
								{formatCost(totalCost)}
							</TableFooterDataCell>
						</tr>
					</tfoot>
				</Table>
			</Stack>
			<Stack gap="2">
				<Text fontWeight="semibold">Breakdown</Text>
				<Table
					headers={[
						analytics.granularity === "MONTHLY" ? "Month" : "Date",
						"Phone",
						"Country",
						"Category",
						"Type",
						"Messages",
						"Cost",
					]}
					alignCols={{ 5: "right", 6: "right" }}
					responsive
					bordered
					hover
					rows={[...points]
						.sort((a, b) => a.start - b.start)
						.map((point: TWAPricingDataPoint) => [
							formatDate(
								new Date(point.start * 1000),
								analytics.granularity === "MONTHLY" ? "MMM YYYY" : "D MMM, YYYY"
							),
							point.phone_number ? `+${point.phone_number}` : "-",
							point.country || "-",
							<PricingLabel value={point.pricing_category} />,
							<PricingLabel value={point.pricing_type} />,
							numberToLocalString(point.volume ?? 0, 0),
							formatCost(point.cost),
						])}
				/>
			</Stack>
			<PricingRawResponse analytics={analytics} />
		</Stack>
	)
}

/**
 * Meta's enum, as Meta names it. Free buckets read as such at a glance.
 */
function PricingLabel({ value }: { value?: string }) {
	if (!value) return <Text color="muted">-</Text>
	const label = value
		.toLowerCase()
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ")
	return value.startsWith("FREE") ? (
		<Badge success>{label}</Badge>
	) : (
		<>{label}</>
	)
}

function PricingRawResponse({ analytics }: { analytics: TWAPricingAnalytics }) {
	const [isVisible, setIsVisible] = useState(false)
	return (
		<Stack gap="2">
			<Box>
				<Button inline onClick={() => setIsVisible((visible) => !visible)}>
					{isVisible ? "Hide" : "Show"} the response from Meta
				</Button>
			</Box>
			{isVisible ? <JSONPreview data={analytics} /> : null}
		</Stack>
	)
}
